import React, { useEffect, useState } from 'react';
import { api } from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Building2, Users, ChevronRight, X, UserPlus, Tag, ToggleLeft, Power, Check, Trash2, AlertTriangle, Unlock, CreditCard, Mail, Sparkles, Receipt, Plus, Phone, MapPin, User } from 'lucide-react';
import SigningMethodToggle from './SigningMethodToggle';

const FEATURES_MAP = {
  invoicing: 'Fakturering',
  payroll: 'Löner',
  bookkeeping: 'Bokföring',
  material_ai: 'Material-AI',
  eta: 'ÄTA-hantering',
  time_reports: 'Tidrapporter',
  machine_hours: 'Maskintimmar',
  daybook: 'Dagbok',
  tenders: 'Upphandlingar',
  compare_offers: 'Jämför Offert',
  support_chat: 'Support Chatt',
};

const SuperAdminCompanies = () => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Dialogs
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [showPricing, setShowPricing] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  // Create User form
  const [userForm, setUserForm] = useState({ name: '', email: '', role: 'admin', password: '', hourly_rate: 0, role_title: '', pin_code: '' });

  // Pricing form
  const [pricingForm, setPricingForm] = useState({ discount_percent: 0, custom_base_price: '', custom_per_employee: '', note: '' });

  // Unlock form
  const [showUnlock, setShowUnlock] = useState(false);
  const [unlockBilling, setUnlockBilling] = useState(null);
  const [unlockLoading, setUnlockLoading] = useState(false);
  const [unlockResult, setUnlockResult] = useState(null);
  const [unlockForm, setUnlockForm] = useState({
    method: 'manual',
    plan_id: 'pro_6',
    monthly_price: '',
    contract_months: 6,
    employee_count: 1,
    coupon: '',
    note: '',
  });

  // Fas 4 (#11): Manuell faktura
  const [showInvoice, setShowInvoice] = useState(false);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [invoiceResult, setInvoiceResult] = useState(null);
  const [pastInvoices, setPastInvoices] = useState([]);
  const [invoiceForm, setInvoiceForm] = useState({
    items: [{ description: '', amount: '', quantity: 1, vat_rate: 25 }],
    days_until_due: 20,
    memo: '',
    email_message: '',
    lock_on_overdue: true,
    issue_date: new Date().toISOString().slice(0, 10),
  });

  const fetchCompanies = async () => {
    try {
      const res = await api.superadminGetCompanies();
      setCompanies((res.data || []).filter(c => c.name !== 'Operio System'));
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchCompanies(); }, []);

  const fetchDetail = async (companyId) => {
    const id = companyId || selectedCompany?.id;
    if (!id) return;
    setDetailLoading(true);
    try {
      const res = await api.superadminGetCompany(id);
      setDetail(res.data);
    } catch (err) { toast.error('Kunde inte hämta företagsdetaljer'); }
    finally { setDetailLoading(false); }
  };

  const selectCompany = async (c) => {
    setSelectedCompany(c);
    await fetchDetail(c.id);
  };

  const closeDetail = () => { setSelectedCompany(null); setDetail(null); };

  const toggleCompany = async (id, active) => {
    try {
      await api.superadminToggleCompany(id, active);
      toast.success(active ? 'Företag aktiverat' : 'Företag pausat');
      fetchCompanies();
      if (detail?.id === id) selectCompany({ id });
    } catch (err) { toast.error('Kunde inte uppdatera'); }
  };

  const handleCreateUser = async () => {
    try {
      await api.superadminCreateUser({ ...userForm, company_id: selectedCompany.id });
      toast.success(`Användare ${userForm.name} skapad`);
      setShowCreateUser(false);
      setUserForm({ name: '', email: '', role: 'admin', password: '', hourly_rate: 0, role_title: '', pin_code: '' });
      selectCompany(selectedCompany);
    } catch (err) { toast.error(err.response?.data?.detail || 'Kunde inte skapa användare'); }
  };

  const handleSavePricing = async () => {
    try {
      const data = {
        discount_percent: parseFloat(pricingForm.discount_percent) || 0,
        custom_base_price: pricingForm.custom_base_price ? parseFloat(pricingForm.custom_base_price) : null,
        custom_per_employee: pricingForm.custom_per_employee ? parseFloat(pricingForm.custom_per_employee) : null,
        note: pricingForm.note || null,
      };
      await api.superadminUpdatePricing(selectedCompany.id, data);
      toast.success('Prissättning uppdaterad');
      setShowPricing(false);
      selectCompany(selectedCompany);
    } catch (err) { toast.error('Kunde inte uppdatera prissättning'); }
  };

  const toggleFeature = async (feature, enabled) => {
    try {
      await api.superadminUpdateFeatures(selectedCompany.id, { features: { [feature]: enabled } });
      toast.success(`${FEATURES_MAP[feature]} ${enabled ? 'aktiverad' : 'inaktiverad'}`);
      selectCompany(selectedCompany);
    } catch (err) { toast.error('Kunde inte uppdatera'); }
  };

  const handleDeleteCompany = async () => {
    if (deleteConfirmText !== selectedCompany.name) {
      toast.error('Företagsnamnet matchar inte');
      return;
    }
    setDeleting(true);
    try {
      const res = await api.superadminDeleteCompany(selectedCompany.id);
      toast.success(res.data?.message || `${selectedCompany.name} raderat`);
      setShowDelete(false);
      setDeleteConfirmText('');
      closeDetail();
      fetchCompanies();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunde inte radera företaget');
    } finally {
      setDeleting(false);
    }
  };

  const openUnlock = async () => {
    setUnlockResult(null);
    setShowUnlock(true);
    setUnlockBilling(null);
    try {
      const res = await api.saCompanyBilling(selectedCompany.id);
      setUnlockBilling(res.data);
      const c = res.data.company || {};
      setUnlockForm({
        method: 'manual',
        plan_id: c.plan_id || 'pro_6',
        monthly_price: c.monthly_price || '',
        contract_months: c.contract_months || 6,
        employee_count: c.employee_count || 1,
        coupon: c.coupon || '',
        note: '',
      });
    } catch (err) {
      toast.error('Kunde inte hämta företagets faktura-info');
    }
  };

  const handleUnlock = async () => {
    setUnlockLoading(true);
    setUnlockResult(null);
    try {
      const payload = {
        method: unlockForm.method,
        plan_id: unlockForm.plan_id || null,
        monthly_price: unlockForm.monthly_price ? parseFloat(unlockForm.monthly_price) : null,
        contract_months: unlockForm.contract_months ? parseInt(unlockForm.contract_months, 10) : null,
        employee_count: unlockForm.employee_count ? parseInt(unlockForm.employee_count, 10) : null,
        coupon: unlockForm.coupon || null,
        note: unlockForm.note || null,
      };
      const res = await api.saUnlockCompany(selectedCompany.id, payload);
      setUnlockResult(res.data);
      toast.success(res.data?.message || 'Kontot upplåst');
      selectCompany(selectedCompany);
      fetchCompanies();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunde inte låsa upp kontot');
    } finally {
      setUnlockLoading(false);
    }
  };

  // Fas 4 (#11): Manuell faktura
  const openInvoice = async () => {
    setInvoiceResult(null);
    setPastInvoices([]);
    setInvoiceForm({
      items: [{ description: '', amount: '', quantity: 1, vat_rate: 25 }],
      days_until_due: 20,
      memo: '',
      email_message: '',
      lock_on_overdue: true,
      issue_date: new Date().toISOString().slice(0, 10),  // default = idag (YYYY-MM-DD)
    });
    setShowInvoice(true);
    try {
      const res = await api.saListManualInvoices(selectedCompany.id);
      setPastInvoices(res.data?.invoices || []);
    } catch (err) {
      // ok — visa bara tom lista
    }
  };

  const addInvoiceRow = () => {
    setInvoiceForm(f => ({ ...f, items: [...f.items, { description: '', amount: '', quantity: 1, vat_rate: 25 }] }));
  };

  const removeInvoiceRow = (idx) => {
    setInvoiceForm(f => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));
  };

  const updateInvoiceRow = (idx, field, value) => {
    setInvoiceForm(f => ({
      ...f,
      items: f.items.map((it, i) => i === idx ? { ...it, [field]: value } : it),
    }));
  };

  const handleSendInvoice = async () => {
    const validItems = invoiceForm.items
      .filter(it => (it.description || '').trim() && parseFloat(it.amount || 0) > 0)
      .map(it => ({
        description: it.description.trim(),
        amount: parseFloat(it.amount),
        quantity: parseFloat(it.quantity) || 1,
        vat_rate: parseFloat(it.vat_rate) || 0,
      }));
    if (validItems.length === 0) {
      toast.error('Lägg till minst en rad med beskrivning och belopp');
      return;
    }
    setInvoiceLoading(true);
    setInvoiceResult(null);
    try {
      const res = await api.saSendManualInvoice(selectedCompany.id, {
        items: validItems,
        days_until_due: parseInt(invoiceForm.days_until_due, 10) || 20,
        memo: invoiceForm.memo || null,
        email_message: invoiceForm.email_message || null,
        lock_on_overdue: invoiceForm.lock_on_overdue,
        issue_date: invoiceForm.issue_date || null,
      });
      setInvoiceResult(res.data);
      toast.success(res.data.message);
      const listRes = await api.saListManualInvoices(selectedCompany.id);
      setPastInvoices(listRes.data?.invoices || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunde inte skicka faktura');
    } finally {
      setInvoiceLoading(false);
    }
  };

  const markInvoicePaid = async (invId) => {
    try {
      await api.saMarkManualInvoicePaid(invId);
      toast.success('Faktura markerad som betald');
      const res = await api.saListManualInvoices(selectedCompany.id);
      setPastInvoices(res.data?.invoices || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunde inte markera betald');
    }
  };

  const invoiceTotals = React.useMemo(() => {
    let ex_vat = 0, vat = 0;
    invoiceForm.items.forEach(it => {
      const amt = parseFloat(it.amount || 0) * (parseFloat(it.quantity) || 1);
      ex_vat += amt;
      vat += amt * (parseFloat(it.vat_rate || 0) / 100);
    });
    return { ex_vat, vat, total: ex_vat + vat };
  }, [invoiceForm.items]);

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-6 h-6 border-2 border-[#E8613A] border-t-transparent op-round rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="flex gap-6" data-testid="sa-companies-page">
      {/* Company list */}
      <div className={`${selectedCompany ? 'w-1/3' : 'w-full'} transition-all`}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold tracking-tight">Företag ({companies.length})</h2>
        </div>
        <div className="space-y-2">
          {companies.map(c => (
            <div key={c.id} onClick={() => selectCompany(c)}
              data-testid={`sa-company-row-${c.id}`}
              className={`bg-card border rounded-lg p-4 cursor-pointer transition-all hover:border-[#E8613A]/40 ${
                selectedCompany?.id === c.id ? 'border-[#E8613A] ring-2 ring-[#E8613A]/10' : 'border-border'
              }`}>
              <div className="flex items-center justify-between">
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-sm text-foreground truncate">{c.name}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{c.org_number || 'Inget org.nr'}</p>
                  {(c.contact_phone || c.contact_email) && (
                    <div className="flex items-center gap-3 mt-1 text-[11px] text-muted-foreground">
                      {c.contact_phone && (
                        <a href={`tel:${c.contact_phone}`} onClick={(e) => e.stopPropagation()}
                          className="flex items-center gap-1 hover:text-[#E8613A]" data-testid={`sa-phone-${c.id}`}>
                          <Phone size={11} /> {c.contact_phone}
                        </a>
                      )}
                      {c.contact_email && (
                        <a href={`mailto:${c.contact_email}`} onClick={(e) => e.stopPropagation()}
                          className="flex items-center gap-1 hover:text-[#E8613A] truncate max-w-[180px]" data-testid={`sa-email-${c.id}`}>
                          <Mail size={11} /> {c.contact_email}
                        </a>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className="text-xs text-muted-foreground"><Users size={12} className="inline mr-1" />{c.user_count}</span>
                  <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${
                    c.active === false ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                  }`}>{c.active === false ? 'Pausad' : 'Aktiv'}</span>
                  <ChevronRight size={14} className="text-muted-foreground" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Detail panel */}
      {selectedCompany && (
        <div className="flex-1 min-w-0">
          <div className="bg-card border border-border rounded-lg overflow-hidden">
            {/* Header */}
            <div className="px-5 py-4 border-b border-border flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#E8613A]/10 flex items-center justify-center">
                  <Building2 size={20} className="text-[#E8613A]" />
                </div>
                <div>
                  <h3 className="font-bold text-foreground">{selectedCompany.name}</h3>
                  <p className="text-xs text-muted-foreground">{detail?.org_number || '—'} | {detail?.work_type || '—'}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={openInvoice}
                  className="text-xs font-medium px-3 py-1.5 rounded-lg transition-colors bg-blue-500/10 text-blue-600 hover:bg-blue-500/20 dark:text-blue-400"
                  data-testid="sa-invoice-company-btn"
                  title="Skapa & skicka en manuell faktura till kunden">
                  <Receipt size={12} className="inline mr-1" />
                  Skapa faktura
                </button>
                <button onClick={openUnlock}
                  className="text-xs font-medium px-3 py-1.5 rounded-lg transition-colors bg-[#E8613A]/10 text-[#E8613A] hover:bg-[#E8613A]/20"
                  data-testid="sa-unlock-company-btn"
                  title="Lås upp / aktivera kontot manuellt eller skicka betallänk">
                  <Unlock size={12} className="inline mr-1" />
                  Lås upp
                </button>
                <button onClick={() => toggleCompany(selectedCompany.id, detail?.active === false)}
                  className={`text-xs font-medium px-3 py-1.5 rounded-lg transition-colors ${
                    detail?.active === false ? 'bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900/30 dark:text-green-400' : 'bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-400'
                  }`} data-testid="sa-toggle-company">
                  <Power size={12} className="inline mr-1" />
                  {detail?.active === false ? 'Aktivera' : 'Pausa'}
                </button>
                <button onClick={() => { setShowDelete(true); setDeleteConfirmText(''); }}
                  className="text-xs font-medium px-3 py-1.5 rounded-lg transition-colors bg-red-600 text-white hover:bg-red-700"
                  data-testid="sa-delete-company-btn"
                  title="Radera företaget permanent">
                  <Trash2 size={12} className="inline mr-1" />
                  Radera
                </button>
                <button onClick={closeDetail} className="p-1.5 text-muted-foreground hover:text-foreground">
                  <X size={16} />
                </button>
              </div>
            </div>

            {detailLoading ? (
              <div className="p-10 text-center">
                <div className="w-6 h-6 border-2 border-[#E8613A] border-t-transparent op-round rounded-full animate-spin mx-auto" />
              </div>
            ) : detail && (
              <div className="p-5 space-y-6">
                {/* Contact info section */}
                <div className="bg-muted/30 rounded-lg p-4 border border-border">
                  <h4 className="text-sm font-bold text-foreground flex items-center gap-1.5 mb-3">
                    <Building2 size={14} /> Kontaktuppgifter
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                    <div className="flex items-start gap-2">
                      <User size={14} className="text-muted-foreground mt-0.5 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Kontaktperson</p>
                        <p className="text-foreground font-medium truncate" data-testid="sa-contact-person">
                          {detail.contact_person || '—'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <Phone size={14} className="text-muted-foreground mt-0.5 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Telefon</p>
                        {detail.contact_phone ? (
                          <a href={`tel:${detail.contact_phone}`}
                            className="text-[#E8613A] hover:underline font-medium"
                            data-testid="sa-contact-phone">
                            {detail.contact_phone}
                          </a>
                        ) : <p className="text-muted-foreground">—</p>}
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <Mail size={14} className="text-muted-foreground mt-0.5 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-[10px] uppercase tracking-wide text-muted-foreground">E-post</p>
                        {detail.contact_email ? (
                          <a href={`mailto:${detail.contact_email}`}
                            className="text-[#E8613A] hover:underline font-medium truncate block"
                            data-testid="sa-contact-email">
                            {detail.contact_email}
                          </a>
                        ) : <p className="text-muted-foreground">—</p>}
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <MapPin size={14} className="text-muted-foreground mt-0.5 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Adress</p>
                        <p className="text-foreground text-xs" data-testid="sa-contact-address">
                          {[detail.address, detail.postal_code, detail.city].filter(Boolean).join(', ') || '—'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Users section */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-bold text-foreground flex items-center gap-1.5"><Users size={14} /> Användare ({detail.users?.length || 0})</h4>
                    <Button size="sm" onClick={() => setShowCreateUser(true)} className="h-8 bg-[#E8613A] hover:bg-[#D4532F] text-white text-xs" data-testid="sa-create-user-btn">
                      <UserPlus size={14} className="mr-1" /> Ny användare
                    </Button>
                  </div>
                  <div className="space-y-1">
                    {(detail.users || []).map(u => (
                      <div key={u.id} className="flex items-center justify-between bg-muted/30 rounded-lg px-3 py-2">
                        <div>
                          <p className="text-sm font-medium text-foreground">{u.name || '—'}</p>
                          <p className="text-xs text-muted-foreground">{u.email}</p>
                        </div>
                        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded ${
                          u.role === 'admin' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                        }`}>{u.role}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Pricing section */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-bold text-foreground flex items-center gap-1.5"><Tag size={14} /> Prissättning</h4>
                    <Button size="sm" variant="outline" onClick={() => {
                      const p = detail.pricing || {};
                      setPricingForm({ discount_percent: p.discount_percent || 0, custom_base_price: p.custom_base_price || '', custom_per_employee: p.custom_per_employee || '', note: p.note || '' });
                      setShowPricing(true);
                    }} className="h-8 text-xs" data-testid="sa-edit-pricing-btn">Redigera</Button>
                  </div>
                  <div className="bg-muted/30 rounded-lg p-3 grid grid-cols-3 gap-3 text-center">
                    <div>
                      <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Rabatt</p>
                      <p className="text-lg font-bold text-foreground">{detail.pricing?.discount_percent || 0}%</p>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Baspris</p>
                      <p className="text-lg font-bold text-foreground">{detail.pricing?.custom_base_price ? `${detail.pricing.custom_base_price} kr` : 'Standard'}</p>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Per anst.</p>
                      <p className="text-lg font-bold text-foreground">{detail.pricing?.custom_per_employee ? `${detail.pricing.custom_per_employee} kr` : 'Standard'}</p>
                    </div>
                  </div>
                  {detail.pricing?.note && <p className="text-xs text-muted-foreground mt-1 italic">{detail.pricing.note}</p>}
                </div>

                {/* Features section */}
                <div>
                  <h4 className="text-sm font-bold text-foreground flex items-center gap-1.5 mb-3"><ToggleLeft size={14} /> Funktioner</h4>
                  <div className="grid grid-cols-2 gap-2">
                    {Object.entries(FEATURES_MAP).map(([key, label]) => {
                      const enabled = detail.features?.[key] !== false;
                      return (
                        <button key={key} onClick={() => toggleFeature(key, !enabled)}
                          data-testid={`sa-feature-${key}`}
                          className={`flex items-center justify-between px-3 py-2.5 rounded-lg border text-left text-sm transition-all ${
                            enabled ? 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-900/20' : 'border-border bg-muted/30 opacity-60'
                          }`}>
                          <span className="font-medium text-foreground text-xs">{label}</span>
                          <div className={`w-8 h-5 rounded-full flex items-center transition-colors ${enabled ? 'bg-green-500 justify-end' : 'bg-muted-foreground/30 justify-start'}`}>
                            <div className="w-3.5 h-3.5 rounded-full bg-white mx-0.5 shadow-sm" />
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <SigningMethodToggle companyId={selectedCompany.id} onChanged={fetchDetail} />

                {/* Subscription */}
                {detail.subscription && (
                  <div>
                    <h4 className="text-sm font-bold text-foreground mb-2">Prenumeration</h4>
                    <div className="bg-muted/30 rounded-lg p-3 text-sm">
                      <p><span className="text-muted-foreground">Plan:</span> <span className="font-medium">{detail.subscription.plan}</span></p>
                      <p><span className="text-muted-foreground">Status:</span> <span className="font-medium">{detail.subscription.status}</span></p>
                      {detail.subscription.employee_count && <p><span className="text-muted-foreground">Anställda:</span> <span className="font-medium">{detail.subscription.employee_count}</span></p>}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Create User Dialog */}
      <Dialog open={showCreateUser} onOpenChange={setShowCreateUser}>
        <DialogContent>
          <DialogHeader><DialogTitle>Ny användare — {selectedCompany?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">Namn</Label><Input value={userForm.name} onChange={e => setUserForm(f => ({ ...f, name: e.target.value }))} className="mt-1" data-testid="sa-user-name" /></div>
            <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">E-post</Label><Input value={userForm.email} onChange={e => setUserForm(f => ({ ...f, email: e.target.value }))} className="mt-1" data-testid="sa-user-email" /></div>
            <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">Roll</Label>
              <select value={userForm.role} onChange={e => setUserForm(f => ({ ...f, role: e.target.value }))} className="w-full h-10 mt-1 rounded-md border border-input bg-background px-3 text-sm" data-testid="sa-user-role">
                <option value="admin">Admin</option>
                <option value="worker">Worker</option>
              </select>
            </div>
            <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">Lösenord</Label><Input value={userForm.password} onChange={e => setUserForm(f => ({ ...f, password: e.target.value }))} className="mt-1" data-testid="sa-user-password" /></div>
            {userForm.role === 'worker' && (
              <>
                <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">PIN-kod (4 siffror)</Label><Input value={userForm.pin_code} onChange={e => setUserForm(f => ({ ...f, pin_code: e.target.value }))} maxLength={4} className="mt-1" /></div>
                <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">Timpris (kr)</Label><Input type="number" value={userForm.hourly_rate} onChange={e => setUserForm(f => ({ ...f, hourly_rate: parseFloat(e.target.value) || 0 }))} className="mt-1" /></div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateUser(false)}>Avbryt</Button>
            <Button onClick={handleCreateUser} className="bg-[#E8613A] hover:bg-[#D4532F] text-white" data-testid="sa-create-user-submit">Skapa</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pricing Dialog */}
      <Dialog open={showPricing} onOpenChange={setShowPricing}>
        <DialogContent>
          <DialogHeader><DialogTitle>Prissättning — {selectedCompany?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">Rabatt (%)</Label><Input type="number" min="0" max="100" value={pricingForm.discount_percent} onChange={e => setPricingForm(f => ({ ...f, discount_percent: e.target.value }))} className="mt-1" data-testid="sa-pricing-discount" /></div>
            <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">Eget baspris (kr/mån) — lämna tomt för standard</Label><Input type="number" value={pricingForm.custom_base_price} onChange={e => setPricingForm(f => ({ ...f, custom_base_price: e.target.value }))} className="mt-1" data-testid="sa-pricing-base" /></div>
            <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">Eget pris per anställd (kr/mån)</Label><Input type="number" value={pricingForm.custom_per_employee} onChange={e => setPricingForm(f => ({ ...f, custom_per_employee: e.target.value }))} className="mt-1" data-testid="sa-pricing-employee" /></div>
            <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">Intern notering</Label><Input value={pricingForm.note} onChange={e => setPricingForm(f => ({ ...f, note: e.target.value }))} className="mt-1" placeholder="T.ex. VIP-kund, avtal 2 år" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPricing(false)}>Avbryt</Button>
            <Button onClick={handleSavePricing} className="bg-[#E8613A] hover:bg-[#D4532F] text-white" data-testid="sa-pricing-save">Spara</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manual Invoice Dialog */}
      <Dialog open={showInvoice} onOpenChange={(o) => { setShowInvoice(o); if (!o) setInvoiceResult(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt size={18} className="text-blue-600" /> Skapa & skicka faktura — {selectedCompany?.name}
            </DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">
              Fakturan skickas som PDF via mejl med Operios bankgiro/IBAN — inte via Stripe.
            </DialogDescription>
          </DialogHeader>

          {!invoiceResult ? (
            <div className="space-y-4">
              {/* Rader */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Fakturarader</Label>
                  <Button size="sm" variant="outline" onClick={addInvoiceRow} data-testid="sa-invoice-add-row"
                    className="h-7 text-xs">
                    <Plus size={12} className="mr-1" /> Lägg till rad
                  </Button>
                </div>
                <div className="space-y-2">
                  {invoiceForm.items.map((it, idx) => (
                    <div key={idx} className="grid grid-cols-[1fr_100px_70px_90px_auto] gap-2 items-start" data-testid={`sa-invoice-row-${idx}`}>
                      <div>
                        <Input
                          placeholder="Beskrivning (t.ex. Onboarding-utbildning 3h)"
                          value={it.description}
                          onChange={(e) => updateInvoiceRow(idx, 'description', e.target.value)}
                          className="h-9 text-sm"
                          data-testid={`sa-invoice-desc-${idx}`}
                        />
                      </div>
                      <div>
                        <Input
                          type="number" step="1" min="0"
                          placeholder="Pris ex moms"
                          value={it.amount}
                          onChange={(e) => updateInvoiceRow(idx, 'amount', e.target.value)}
                          className="h-9 text-sm text-right"
                          data-testid={`sa-invoice-amount-${idx}`}
                        />
                      </div>
                      <div>
                        <Input
                          type="number" step="0.5" min="0.5"
                          value={it.quantity}
                          onChange={(e) => updateInvoiceRow(idx, 'quantity', e.target.value)}
                          className="h-9 text-sm text-center"
                          placeholder="Antal"
                        />
                      </div>
                      <div>
                        <select
                          value={it.vat_rate}
                          onChange={(e) => updateInvoiceRow(idx, 'vat_rate', e.target.value)}
                          className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
                          data-testid={`sa-invoice-vat-${idx}`}
                        >
                          <option value={25}>25%</option>
                          <option value={12}>12%</option>
                          <option value={6}>6%</option>
                          <option value={0}>0%</option>
                        </select>
                      </div>
                      <div>
                        {invoiceForm.items.length > 1 && (
                          <Button size="sm" variant="ghost" onClick={() => removeInvoiceRow(idx)}
                            className="h-9 w-9 p-0 text-red-500 hover:text-red-700"
                            data-testid={`sa-invoice-remove-${idx}`}>
                            <X size={14} />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-[1fr_100px_70px_90px_auto] gap-2 text-[10px] text-muted-foreground pt-1">
                  <span>Beskrivning</span>
                  <span className="text-right">Pris ex moms</span>
                  <span className="text-center">Antal</span>
                  <span>Moms</span>
                  <span></span>
                </div>
              </div>

              {/* Totalt */}
              <div className="bg-blue-50/50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-900 rounded-lg p-3 text-sm space-y-1">
                <div className="flex justify-between text-muted-foreground">
                  <span>Belopp exkl. moms</span>
                  <span data-testid="sa-invoice-total-ex">{invoiceTotals.ex_vat.toLocaleString('sv-SE', { maximumFractionDigits: 2 })} kr</span>
                </div>
                <div className="flex justify-between text-muted-foreground">
                  <span>Moms</span>
                  <span data-testid="sa-invoice-total-vat">{invoiceTotals.vat.toLocaleString('sv-SE', { maximumFractionDigits: 2 })} kr</span>
                </div>
                <div className="flex justify-between font-bold text-foreground border-t border-blue-200 dark:border-blue-900 pt-1">
                  <span>Att betala</span>
                  <span data-testid="sa-invoice-total-inc">{invoiceTotals.total.toLocaleString('sv-SE', { maximumFractionDigits: 2 })} kr</span>
                </div>
              </div>

              {/* Övriga inställningar */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Fakturadatum</Label>
                  <Input
                    type="date"
                    value={invoiceForm.issue_date}
                    onChange={(e) => setInvoiceForm(f => ({ ...f, issue_date: e.target.value }))}
                    className="mt-1"
                    data-testid="sa-invoice-issue-date"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Förfaller om (dagar)</Label>
                  <Input
                    type="number" min="1" max="90"
                    value={invoiceForm.days_until_due}
                    onChange={(e) => setInvoiceForm(f => ({ ...f, days_until_due: e.target.value }))}
                    className="mt-1"
                    data-testid="sa-invoice-days"
                  />
                </div>
                <div className="flex items-end col-span-2">
                  <label className="flex items-center gap-2 pb-1 cursor-pointer">
                    <input type="checkbox"
                      checked={invoiceForm.lock_on_overdue}
                      onChange={(e) => setInvoiceForm(f => ({ ...f, lock_on_overdue: e.target.checked }))}
                      className="w-4 h-4"
                      data-testid="sa-invoice-lock" />
                    <span className="text-xs">Lås kontot om obetald efter förfallodatum</span>
                  </label>
                </div>
              </div>

              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Fakturanotering (visas på fakturan)</Label>
                <Input
                  value={invoiceForm.memo}
                  onChange={(e) => setInvoiceForm(f => ({ ...f, memo: e.target.value }))}
                  placeholder="T.ex. Refererar till avtal från 2026-06-01"
                  className="mt-1"
                  data-testid="sa-invoice-memo"
                />
              </div>

              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Personligt meddelande i email (valfritt)</Label>
                <textarea
                  value={invoiceForm.email_message}
                  onChange={(e) => setInvoiceForm(f => ({ ...f, email_message: e.target.value }))}
                  placeholder="Skriv ett kort meddelande som skickas med fakturan"
                  rows={3}
                  className="w-full mt-1 rounded-md border border-input bg-background px-3 py-2 text-sm resize-none"
                  data-testid="sa-invoice-email-msg"
                />
              </div>

              {/* Historik */}
              {pastInvoices.length > 0 && (
                <div className="border-t border-border pt-3">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Tidigare manuella fakturor ({pastInvoices.length})</Label>
                  <div className="space-y-1.5 mt-2 max-h-40 overflow-y-auto">
                    {pastInvoices.map(inv => (
                      <div key={inv.id} className="flex items-center justify-between text-xs bg-muted/30 rounded p-2" data-testid={`sa-past-invoice-${inv.id}`}>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">
                            {inv.invoice_number ? `${inv.invoice_number} · ` : ''}{inv.memo || `${inv.items?.length || 0} rader`}
                          </p>
                          <p className="text-[10px] text-muted-foreground">
                            {new Date(inv.created_at).toLocaleDateString('sv-SE')} · {Math.round(inv.amount_due || 0)} kr · {inv.status === 'paid' ? '✅ Betald' : inv.status === 'email_failed' ? '⚠️ E-post misslyckades' : '⏳ Skickad'}
                          </p>
                        </div>
                        <div className="flex items-center gap-1">
                          <a href={api.saDownloadManualInvoicePdf(inv.id)} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-[10px] px-2">PDF</a>
                          {inv.status !== 'paid' && (
                            <Button size="sm" variant="ghost" onClick={() => markInvoicePaid(inv.id)} className="h-6 text-[10px] text-green-600 hover:bg-green-50 dark:hover:bg-green-950 px-2">
                              Markera betald
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            /* Success-vy */
            <div className="bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-900 rounded-lg p-4 space-y-2">
              <div className="flex items-center gap-2">
                <Check size={18} className="text-green-600" />
                <p className="font-semibold text-green-800 dark:text-green-200">{invoiceResult.message}</p>
              </div>
              <p className="text-sm">
                Fakturanr: <strong>{invoiceResult.invoice_number}</strong> — {invoiceResult.amount_due?.toLocaleString('sv-SE')} kr (varav {Math.round(invoiceResult.vat_amount || 0)} kr moms)
              </p>
              {invoiceResult.manual_invoice_id && (
                <a href={api.saDownloadManualInvoicePdf(invoiceResult.manual_invoice_id)} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-sm font-semibold text-blue-600 hover:underline"
                  data-testid="sa-invoice-open-link">
                  Ladda ner PDF ↓
                </a>
              )}
              {invoiceResult.email_error && (
                <p className="text-xs text-amber-700 dark:text-amber-400 mt-1">⚠️ E-post: {invoiceResult.email_error}</p>
              )}
            </div>
          )}

          <DialogFooter>
            {!invoiceResult ? (
              <>
                <Button variant="outline" onClick={() => setShowInvoice(false)} disabled={invoiceLoading}>Avbryt</Button>
                <Button onClick={handleSendInvoice} disabled={invoiceLoading}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="sa-invoice-send">
                  {invoiceLoading ? 'Skickar…' : `Skapa & skicka faktura (${invoiceTotals.total.toLocaleString('sv-SE', { maximumFractionDigits: 0 })} kr)`}
                </Button>
              </>
            ) : (
              <Button onClick={() => { setInvoiceResult(null); }} className="bg-blue-600 hover:bg-blue-700 text-white">Skapa ny faktura</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unlock Company Dialog */}
      <Dialog open={showUnlock} onOpenChange={(o) => { setShowUnlock(o); if (!o) { setUnlockResult(null); setUnlockBilling(null); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Unlock size={18} className="text-[#E8613A]" /> Lås upp {selectedCompany?.name}
            </DialogTitle>
          </DialogHeader>

          {/* Status-summering */}
          {unlockBilling && (
            <div className="bg-muted/30 rounded-lg p-3 text-xs space-y-1 mb-2">
              <p>
                <span className="text-muted-foreground">Status: </span>
                <span className={`font-semibold ${unlockBilling.is_locked ? 'text-red-600' : 'text-green-600'}`}>
                  {unlockBilling.is_locked ? 'LÅST (paywall aktiv)' : 'Upplåst'}
                </span>
              </p>
              <p>
                <span className="text-muted-foreground">Skapat: </span>
                <span className="font-medium">{unlockBilling.days_since_created ?? '–'} dagar sedan</span>
                {' · '}
                <span className="text-muted-foreground">Sub-status: </span>
                <span className="font-medium">{unlockBilling.company?.subscription_status || 'ingen'}</span>
              </p>
              {unlockBilling.company?.unlock_method && (
                <p className="text-muted-foreground italic">
                  Senast upplåst manuellt ({unlockBilling.company.unlock_method}) — {unlockBilling.company.unlock_note || 'ingen notering'}
                </p>
              )}
            </div>
          )}

          {!unlockResult ? (
            <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
              {/* Metod-val */}
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Metod</Label>
                <div className="grid grid-cols-3 gap-2 mt-1.5">
                  {[
                    { v: 'manual', label: 'Manuell', icon: Sparkles, desc: 'Aktivera direkt utan Stripe' },
                    { v: 'stripe_checkout', label: 'Skicka kort­länk', icon: CreditCard, desc: 'Kund betalar med kort' },
                    { v: 'stripe_invoice', label: 'Faktura', icon: Mail, desc: 'Stripe mejlar faktura (14d)' },
                  ].map(opt => {
                    const Icon = opt.icon;
                    const active = unlockForm.method === opt.v;
                    return (
                      <button
                        key={opt.v}
                        onClick={() => setUnlockForm(f => ({ ...f, method: opt.v }))}
                        data-testid={`sa-unlock-method-${opt.v}`}
                        className={`flex flex-col items-center text-center gap-1 p-2.5 rounded-lg border text-xs transition-all ${
                          active
                            ? 'border-[#E8613A] bg-[#E8613A]/5 ring-2 ring-[#E8613A]/20'
                            : 'border-border bg-card hover:border-[#E8613A]/40'
                        }`}
                      >
                        <Icon size={16} className={active ? 'text-[#E8613A]' : 'text-muted-foreground'} />
                        <span className="font-semibold text-foreground">{opt.label}</span>
                        <span className="text-[10px] text-muted-foreground leading-tight">{opt.desc}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Plan + pris */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Paket</Label>
                  <select
                    value={unlockForm.plan_id}
                    onChange={e => setUnlockForm(f => ({ ...f, plan_id: e.target.value }))}
                    className="w-full h-10 mt-1 rounded-md border border-input bg-background px-3 text-sm"
                    data-testid="sa-unlock-plan-id"
                  >
                    <option value="start_6">Start — 6 mån</option>
                    <option value="start_12">Start — 12 mån</option>
                    <option value="start_24">Start — 24 mån</option>
                    <option value="pro_6">Pro — 6 mån</option>
                    <option value="pro_12">Pro — 12 mån</option>
                    <option value="pro_24">Pro — 24 mån</option>
                    <option value="business_6">Business — 6 mån</option>
                    <option value="business_12">Business — 12 mån</option>
                    <option value="business_24">Business — 24 mån</option>
                  </select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Bindning (mån)</Label>
                  <Input
                    type="number"
                    min="6" max="24"
                    value={unlockForm.contract_months}
                    onChange={e => setUnlockForm(f => ({ ...f, contract_months: e.target.value }))}
                    className="mt-1"
                    data-testid="sa-unlock-contract"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Månadspris (kr ex moms)</Label>
                  <Input
                    type="number"
                    min="0" step="1"
                    value={unlockForm.monthly_price}
                    onChange={e => setUnlockForm(f => ({ ...f, monthly_price: e.target.value }))}
                    placeholder="t.ex. 1196"
                    className="mt-1"
                    data-testid="sa-unlock-price"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Anställda</Label>
                  <Input
                    type="number" min="1"
                    value={unlockForm.employee_count}
                    onChange={e => setUnlockForm(f => ({ ...f, employee_count: e.target.value }))}
                    className="mt-1"
                  />
                </div>
              </div>

              {unlockForm.method === 'stripe_checkout' && (
                <div>
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Rabattkod (Stripe Promotion Code) — valfritt</Label>
                  <Input
                    value={unlockForm.coupon}
                    onChange={e => setUnlockForm(f => ({ ...f, coupon: e.target.value }))}
                    placeholder="t.ex. HAMPUS60"
                    className="mt-1"
                  />
                </div>
              )}

              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Intern notering</Label>
                <Input
                  value={unlockForm.note}
                  onChange={e => setUnlockForm(f => ({ ...f, note: e.target.value }))}
                  placeholder="t.ex. Specialavtal med rabatt — signerat utanför systemet"
                  className="mt-1"
                  data-testid="sa-unlock-note"
                />
              </div>

              {/* Pris-förhandsvisning */}
              {unlockForm.monthly_price && (
                <div className="bg-[#E8613A]/5 border border-[#E8613A]/20 rounded-lg p-3 text-xs space-y-0.5">
                  <p className="flex items-center justify-between">
                    <span>Månadsavgift</span>
                    <span className="font-semibold">{Number(unlockForm.monthly_price).toLocaleString('sv-SE')} kr</span>
                  </p>
                  <p className="flex items-center justify-between text-muted-foreground">
                    <span>Moms 25%</span>
                    <span>{(parseFloat(unlockForm.monthly_price || 0) * 0.25).toLocaleString('sv-SE')} kr</span>
                  </p>
                  <p className="flex items-center justify-between font-bold text-foreground border-t border-[#E8613A]/20 pt-1 mt-1">
                    <span>Att betala / mån</span>
                    <span>{(parseFloat(unlockForm.monthly_price || 0) * 1.25).toLocaleString('sv-SE')} kr</span>
                  </p>
                </div>
              )}
            </div>
          ) : (
            /* Success-vy */
            <div className="space-y-3">
              <div className="bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-900 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Check size={18} className="text-green-600" />
                  <p className="font-semibold text-green-800 dark:text-green-200 text-sm">{unlockResult.message}</p>
                </div>
                {unlockResult.checkout_url && (
                  <div className="mt-3">
                    <p className="text-xs text-muted-foreground mb-1">Betallänk (skickad till kunden):</p>
                    <div className="flex gap-2">
                      <Input value={unlockResult.checkout_url} readOnly className="text-xs font-mono" />
                      <Button
                        size="sm"
                        onClick={() => { navigator.clipboard.writeText(unlockResult.checkout_url); toast.success('Länk kopierad'); }}
                        className="bg-[#E8613A] hover:bg-[#D4532F] text-white"
                      >
                        Kopiera
                      </Button>
                    </div>
                  </div>
                )}
                {unlockResult.hosted_invoice_url && (
                  <div className="mt-3">
                    <p className="text-xs text-muted-foreground mb-1">Faktura ({Number(unlockResult.amount_due || 0).toLocaleString('sv-SE')} kr):</p>
                    <a
                      href={unlockResult.hosted_invoice_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#E8613A] hover:underline"
                    >
                      Öppna faktura i Stripe ↗
                    </a>
                  </div>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            {!unlockResult ? (
              <>
                <Button variant="outline" onClick={() => setShowUnlock(false)} disabled={unlockLoading}>Avbryt</Button>
                <Button
                  onClick={handleUnlock}
                  disabled={unlockLoading || !unlockForm.monthly_price}
                  className="bg-[#E8613A] hover:bg-[#D4532F] text-white"
                  data-testid="sa-unlock-submit"
                >
                  {unlockLoading
                    ? 'Behandlar…'
                    : unlockForm.method === 'manual' ? 'Aktivera nu'
                    : unlockForm.method === 'stripe_checkout' ? 'Skapa & skicka betallänk'
                    : 'Skicka faktura'}
                </Button>
              </>
            ) : (
              <Button onClick={() => setShowUnlock(false)} className="bg-[#E8613A] hover:bg-[#D4532F] text-white">Stäng</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Company Dialog */}
      <Dialog open={showDelete} onOpenChange={(o) => { setShowDelete(o); if (!o) setDeleteConfirmText(''); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle size={18} /> Radera företag permanent?
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p>
              Du är på väg att radera <strong>{selectedCompany?.name}</strong> och all associerad data:
            </p>
            <ul className="list-disc list-inside text-xs text-muted-foreground bg-muted/30 rounded-lg p-3 space-y-0.5">
              <li>Alla användare, projekt, anställda &amp; maskiner</li>
              <li>Fakturor, ÄTA, tidrapporter, dagböcker, löner</li>
              <li>Bokföring (verifikationer, periodstängningar)</li>
              <li>Chattar, kundåtkomster, signeringar</li>
              <li>Aktiv Stripe-prenumeration avslutas automatiskt</li>
            </ul>
            <p className="text-xs text-red-600 font-semibold flex items-center gap-1.5">
              <AlertTriangle size={12} /> Detta går inte att ångra.
            </p>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                Skriv <code className="px-1 rounded bg-muted text-foreground">{selectedCompany?.name}</code> för att bekräfta
              </Label>
              <Input
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder={selectedCompany?.name || ''}
                className="mt-1.5"
                data-testid="sa-delete-confirm-input"
                autoFocus
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowDelete(false); setDeleteConfirmText(''); }}>Avbryt</Button>
            <Button
              onClick={handleDeleteCompany}
              disabled={deleting || deleteConfirmText !== selectedCompany?.name}
              className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
              data-testid="sa-delete-confirm-btn"
            >
              {deleting ? 'Raderar…' : 'Radera permanent'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SuperAdminCompanies;
